import { EFinancingCompany } from '@if/core/types'
import { Tab, Tabs } from '@mui/material'
import Breadcrumb from '@src/components/Breadcrumb'
import ReportsList from '@src/components/reportsList'
import TabPanel from '@src/components/TabPanel'
import Users from '@src/components/userList'
import {
  useGetMerchantReports,
  useMerchantById,
  useMerchantUserList,
  useMerchantsList,
} from '@src/data/api/merchants-api/merchants-api'
import { EMerchantReportFrequency } from '@src/data/types/Constants'
import { MerchantFilter } from '@src/data/types/Filter'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import BannerInformation from './bannerInformation'
import Merchants from './merchantList'

const BannerPage = () => {
  const { t } = useTranslation()
  const { id } = useParams()

  const MERCHANTS_LIMIT = 1000

  const [users, isFetchingUsers] = useMerchantUserList(id!)
  const [banner] = useMerchantById(id!)

  // Scope the lookup to the banner's own financing company. Without it the API falls back
  // to IFinance for any user who has access to more than one, so an Iceberg banner's
  // children are filtered out and the grid comes back empty.
  const query: MerchantFilter = useMemo(() => {
    return {
      parentId: id!,
      limit: MERCHANTS_LIMIT,
      offset: 0,
      financingCompanyId: banner?.financingCompanyId,
    } as MerchantFilter
  }, [id, banner?.financingCompanyId])

  const [merchantsList, isFetchingMerchantsList] = useMerchantsList(query, !!banner)
  const [shouldPoll, setShouldPoll] = useState(false)
  const [reportsSummary, isFetchingReports] = useGetMerchantReports(id ?? '', shouldPoll)
  const [selectedBannerTypeId, setSelectedBannerTypeId] = useState(0)

  const handleTabChange = (_event: ChangeEvent<object>, newValue: number) => {
    setSelectedBannerTypeId(newValue)
  }

  const handleGenerateNewReport = () => {
    setShouldPoll(true)
  }

  useEffect(() => {
    if (reportsSummary && reportsSummary.length > 0 && shouldPoll === true) {
      const latestReport = reportsSummary[0]
      const reportDate = new Date(latestReport.createdOn)

      // Check if the latest report is recent within the 10 seconds
      if (Date.now() - reportDate.getTime() < 10 * 1000) {
        setShouldPoll(false)
      }
    }
  }, [shouldPoll, reportsSummary])

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    {
      path: `/banner/${banner?.id}/view`,
      label: t('breadcrumbs.banner').concat(` : ${banner?.name}`),
    },
  ]

  return (
    <>
      <div style={{ marginLeft: '15px' }}>
        <Breadcrumb trees={breadCrumbs} />
      </div>
      <BannerInformation banner={banner} />

      <div style={{ display: 'flex', justifyContent: 'left', marginLeft: '30px' }}>
        <Tabs value={selectedBannerTypeId} onChange={handleTabChange}>
          <Tab label={t('common.user')} />
          <Tab label={t('common.merchant')} />
          {banner?.financingCompanyId === EFinancingCompany.IFinance && <Tab label={t('common.reports')} />}
        </Tabs>
      </div>
      <TabPanel value={selectedBannerTypeId} index={0}>
        <Users merchantId={id!} usersList={users} isPendingSearchUsers={isFetchingUsers} />
      </TabPanel>
      <TabPanel value={selectedBannerTypeId} index={1}>
        <Merchants banner={banner} merchantsList={merchantsList} isFetchingMerchantsList={isFetchingMerchantsList} />
      </TabPanel>
      {banner?.financingCompanyId === EFinancingCompany.IFinance && (
        <TabPanel value={selectedBannerTypeId} index={2}>
          <ReportsList
            merchantId={id!}
            reportsSummary={reportsSummary}
            isFetchingReports={isFetchingReports || shouldPoll}
            onGenerateReport={handleGenerateNewReport}
            generateReportDisabled={
              !banner?.reportGenerationFrequency || banner.reportGenerationFrequency === EMerchantReportFrequency.never
            }
          />
        </TabPanel>
      )}
    </>
  )
}

export default BannerPage
